import type {
  AutocompleteRequest,
  AutocompleteSuggestion,
  CardAutocompleteRequest,
  CardAutocompleteSuggestion,
  CreateDatabaseRequest,
  Database,
  DatabaseId,
  DatabaseUsageInfo,
  Engine,
  Field,
  GetDatabaseHealthRequest,
  GetDatabaseHealthResponse,
  GetDatabaseMetadataRequest,
  GetDatabaseRequest,
  GetDatabaseSettingsAvailableResponse,
  ListDatabaseIdFieldsRequest,
  ListDatabaseSchemaTablesRequest,
  ListDatabaseSchemasRequest,
  ListDatabasesRequest,
  ListDatabasesResponse,
  ListVirtualDatabaseTablesRequest,
  SchemaName,
  Table,
  UpdateDatabaseRequest,
  ValidateDatabaseRequest,
  ValidateDatabaseResponse,
} from "metabase-types/api";

import { Api } from "./api";
import {
  idTag,
  invalidateTags,
  listTag,
  provideAutocompleteSuggestionListTags,
  provideCardAutocompleteSuggestionListTags,
  provideDatabaseListTags,
  provideDatabaseTags,
  tag,
} from "./tags";

/**
 * schema names containing slashes, backslashes, or percent signs are rejected
 * at the HTTP layer when percent-encoded in a URL path, so they must be passed
 * as a query parameter instead (#77353)
 */
export const shouldSchemaBePassedAsQueryParam = (schema: SchemaName) =>
  /[/\\%]/.test(schema);

export const databaseApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    listEngines: builder.query<Record<string, Engine>, void>({
      query: () => ({
        method: "GET",
        url: "/api/setting/engines",
      }),
      // Driver metadata only changes when the instance is upgraded.
      keepUnusedDataFor: Infinity,
    }),
    listDatabases: builder.query<
      ListDatabasesResponse,
      ListDatabasesRequest | void
    >({
      query: (params) => ({
        method: "GET",
        url: "/api/database",
        params,
      }),
      providesTags: (response) => provideDatabaseListTags(response?.data ?? []),
    }),
    getDatabase: builder.query<Database, GetDatabaseRequest>({
      query: ({ id, ...params }) => ({
        method: "GET",
        url: `/api/database/${id}`,
        params,
      }),
      providesTags: (database) =>
        database ? provideDatabaseTags(database) : [],
    }),
    getDatabaseHealth: builder.query<
      GetDatabaseHealthResponse,
      GetDatabaseHealthRequest
    >({
      query: ({ id, ...params }) => ({
        method: "GET",
        url: `/api/database/${id}/healthcheck`,
        params,
      }),
      // invalidate health check in the case db connection info changes
      providesTags: (_, __, { id }) => [idTag("database", id)],
    }),
    getDatabaseMetadata: builder.query<Database, GetDatabaseMetadataRequest>({
      query: ({ id, ...params }) => ({
        method: "GET",
        url: `/api/database/${id}/metadata`,
        params,
      }),
      providesTags: (database) =>
        database ? provideDatabaseTags(database) : [],
    }),
    getDatabaseUsageInfo: builder.query<DatabaseUsageInfo, DatabaseId>({
      query: (id) => ({
        method: "GET",
        url: `/api/database/${id}/usage_info`,
      }),
      providesTags: (_response, _error, id) => [idTag("database", id)],
    }),
    getDatabaseSettingsAvailable: builder.query<
      GetDatabaseSettingsAvailableResponse,
      DatabaseId
    >({
      query: (id) => ({
        method: "GET",
        url: `/api/database/${id}/settings-available`,
      }),
      providesTags: (_response, _error, id) => [idTag("database", id)],
    }),
    listDatabaseSchemas: builder.query<
      SchemaName[],
      ListDatabaseSchemasRequest
    >({
      query: ({ id, ...params }) => ({
        method: "GET",
        url: `/api/database/${id}/schemas`,
        params,
      }),
      providesTags: (schemas = []) => [
        listTag("schema"),
        ...schemas.map((schema) => idTag("schema", schema)),
      ],
    }),
    listSyncableDatabaseSchemas: builder.query<SchemaName[], DatabaseId>({
      query: (id) => ({
        method: "GET",
        url: `/api/database/${id}/syncable_schemas`,
      }),
      providesTags: (schemas = []) => [
        listTag("schema"),
        ...schemas.map((schema) => idTag("schema", schema)),
      ],
    }),
    listDatabaseSchemaTables: builder.query<
      Table[],
      ListDatabaseSchemaTablesRequest
    >({
      query: ({ id, schema, ...params }) => {
        const isQueryParam = shouldSchemaBePassedAsQueryParam(schema);

        return {
          method: "GET",
          url: isQueryParam
            ? `/api/database/${id}/schema/`
            : `/api/database/${id}/schema/${encodeURIComponent(schema)}`,
          params: isQueryParam ? { ...params, schema } : params,
        };
      },
      providesTags: (tables = []) => [
        listTag("table"),
        ...tables.map((table) => idTag("table", table.id)),
      ],
    }),
    listVirtualDatabaseTables: builder.query<
      Table[],
      ListVirtualDatabaseTablesRequest
    >({
      query: ({ id, schema, ...params }) => ({
        method: "GET",
        url: `/api/database/${id}/datasets/${encodeURIComponent(schema)}`,
        params,
      }),
      providesTags: (tables = []) => [
        listTag("table"),
        ...tables.map((table) => idTag("table", table.id)),
      ],
    }),
    listDatabaseIdFields: builder.query<Field[], ListDatabaseIdFieldsRequest>({
      query: ({ id, ...params }) => ({
        method: "GET",
        url: `/api/database/${id}/idfields`,
        params,
      }),
      providesTags: [listTag("field")],
    }),
    validateDatabase: builder.mutation<
      ValidateDatabaseResponse,
      ValidateDatabaseRequest
    >({
      query: (body) => ({
        method: "POST",
        url: "/api/database/validate",
        body,
      }),
    }),
    createDatabase: builder.mutation<Database, CreateDatabaseRequest>({
      query: (body) => ({
        method: "POST",
        url: "/api/database",
        body,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [
          listTag("database"),
          listTag("setup-guide-checklist"),
        ]),
    }),
    updateDatabase: builder.mutation<Database, UpdateDatabaseRequest>({
      query: ({ id, ...body }) => ({
        method: "PUT",
        url: `/api/database/${id}`,
        body,
      }),
      invalidatesTags: (_, error, { id }) =>
        invalidateTags(error, [
          listTag("database"),
          idTag("database", id),
          tag("table"),
          tag("field"),
          tag("field-values"),
          tag("parameter-values"),
          tag("card"),
        ]),
    }),
    deleteDatabase: builder.mutation<void, DatabaseId>({
      query: (id) => ({
        method: "DELETE",
        url: `/api/database/${id}`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [
          listTag("database"),
          idTag("database", id),
          tag("table"),
          tag("field"),
          tag("field-values"),
          tag("parameter-values"),
          tag("card"),
        ]),
    }),
    persistDatabase: builder.mutation<void, DatabaseId>({
      query: (id) => ({
        method: "POST",
        url: `/api/persist/database/${id}/persist`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [idTag("database", id)]),
    }),
    unpersistDatabase: builder.mutation<void, DatabaseId>({
      query: (id) => ({
        method: "POST",
        url: `/api/persist/database/${id}/unpersist`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [idTag("database", id)]),
    }),
    dismissDatabaseSyncSpinner: builder.mutation<void, DatabaseId>({
      query: (id) => ({
        method: "POST",
        url: `/api/database/${id}/dismiss_spinner`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [listTag("database"), idTag("database", id)]),
    }),
    syncDatabaseSchema: builder.mutation<void, DatabaseId>({
      query: (databaseId) => ({
        method: "POST",
        url: `/api/database/${databaseId}/sync_schema`,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [
          tag("schema"),
          tag("table"),
          tag("field"),
          tag("field-values"),
          tag("parameter-values"),
          tag("card"),
        ]),
    }),
    rescanDatabaseFieldValues: builder.mutation<void, DatabaseId>({
      query: (databaseId) => ({
        method: "POST",
        url: `/api/database/${databaseId}/rescan_values`,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [tag("field-values"), tag("parameter-values")]),
    }),
    discardDatabaseFieldValues: builder.mutation<void, DatabaseId>({
      query: (databaseId) => ({
        method: "POST",
        url: `/api/database/${databaseId}/discard_values`,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [tag("field-values"), tag("parameter-values")]),
    }),
    addSampleDatabase: builder.mutation<Database, void>({
      query: () => ({
        method: "POST",
        url: `/api/database/sample_database`,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [listTag("database")]),
    }),
    listAutocompleteSuggestions: builder.query<
      AutocompleteSuggestion[],
      AutocompleteRequest
    >({
      query: ({ databaseId, ...params }) => ({
        method: "GET",
        url: `/api/database/${databaseId}/autocomplete_suggestions`,
        params,
      }),
      providesTags: () => provideAutocompleteSuggestionListTags(),
    }),
    listCardAutocompleteSuggestions: builder.query<
      CardAutocompleteSuggestion[],
      CardAutocompleteRequest
    >({
      query: ({ databaseId, ...params }) => ({
        method: "GET",
        url: `/api/database/${databaseId}/card_autocomplete_suggestions`,
        params,
      }),
      providesTags: () => provideCardAutocompleteSuggestionListTags(),
    }),
  }),
});

export const {
  useListDatabasesQuery,
  useListEnginesQuery,
  useLazyListDatabasesQuery,
  useGetDatabaseQuery,
  useGetDatabaseHealthQuery,
  useGetDatabaseMetadataQuery,
  useGetDatabaseUsageInfoQuery,
  useGetDatabaseSettingsAvailableQuery,
  useListDatabaseSchemasQuery,
  useLazyListDatabaseSchemasQuery,
  usePrefetch: useDatabasePrefetch,
  useListSyncableDatabaseSchemasQuery,
  useListDatabaseSchemaTablesQuery,
  useLazyListDatabaseSchemaTablesQuery,
  useListVirtualDatabaseTablesQuery,
  useListDatabaseIdFieldsQuery,
  useValidateDatabaseMutation,
  useCreateDatabaseMutation,
  useUpdateDatabaseMutation,
  useDeleteDatabaseMutation,
  usePersistDatabaseMutation,
  useUnpersistDatabaseMutation,
  useDismissDatabaseSyncSpinnerMutation,
  useSyncDatabaseSchemaMutation,
  useRescanDatabaseFieldValuesMutation,
  useDiscardDatabaseFieldValuesMutation,
  useListAutocompleteSuggestionsQuery,
  useLazyListAutocompleteSuggestionsQuery,
  useAddSampleDatabaseMutation,
  useListCardAutocompleteSuggestionsQuery,
  useLazyListCardAutocompleteSuggestionsQuery,
} = databaseApi;
